import * as Sentry from '@sentry/react'

/**
 * Build metadata as exposed to the running client.
 *
 * `version` and `environment` are always present — they're the public-safe
 * fields returned by the anonymous `/version` API endpoint. Everything else
 * (service codename, commit SHA, build timestamp) is optional because the
 * anonymous endpoint deliberately omits it to avoid leaking the internal
 * codename or pinpointing the exact build SHA a client is running. Those
 * fields are populated only when (a) we're reading the bundle-baked client
 * buildInfo or (b) we're hitting an authorized endpoint that returns the
 * full payload.
 */
export type BuildInfo = {
  version: string
  environment: string
  service?: string
  commit?: string
  commitShort?: string
  buildDate?: string
}

/**
 * This client's build info — baked into the JS bundle at build time via
 * Vite `define` in vite.config.ts. Falls back to {} only if a hot reload
 * picks up a stale module; the typing in vite-env.d.ts guarantees the
 * shape in compiled output.
 */
export const buildInfo: BuildInfo =
  typeof __BUILD_INFO__ !== 'undefined'
    ? __BUILD_INFO__
    : {
        service: 'moly-app',
        version: '0.0.0',
        commit: 'local',
        commitShort: 'local',
        buildDate: 'local',
        environment: 'development',
      }

/**
 * Same payload as compiled in, but fetched from the static asset at the
 * site root. Used to detect a deployed build that's newer than the JS
 * bundle a long-lived tab is running. Reserved for future use.
 */
export async function fetchDeployedBuildInfo(): Promise<BuildInfo | null> {
  try {
    const res = await fetch('/build-info.json', { cache: 'no-store' })
    if (!res.ok) return null
    return (await res.json()) as BuildInfo
  } catch {
    return null
  }
}

/**
 * Fetch the API's build info from its anonymous /version endpoint.
 * Returns null on failure — the caller should not surface errors here;
 * this is best-effort telemetry, not a user-facing flow.
 */
export async function fetchApiBuildInfo(): Promise<BuildInfo | null> {
  const baseUrl = import.meta.env.VITE_MOLY_API
  if (!baseUrl) return null

  try {
    const res = await fetch(`${baseUrl.replace(/\/$/, '')}/version`, {
      cache: 'no-store',
    })
    if (!res.ok) return null
    return (await res.json()) as BuildInfo
  } catch {
    return null
  }
}

/**
 * Logs the client/API build pair to Sentry on app boot. No user-facing
 * UX; just enriches every subsequent error with the stack-skew context.
 *
 * Boot runs unauthenticated, so we fetch the sanitised `/version` payload —
 * which intentionally drops commit SHA and build date. API-side commit info
 * still reaches Sentry via the API's own server-side instrumentation; we
 * only tag what the public endpoint exposes.
 */
export async function reportBuildPairToSentry(): Promise<void> {
  const api = await fetchApiBuildInfo()
  if (!api) return

  Sentry.setTag('api.environment', api.environment)
  if (buildInfo.commitShort) {
    Sentry.setTag('client.commit', buildInfo.commitShort)
  }

  Sentry.addBreadcrumb({
    category: 'build',
    level: 'info',
    message: 'stack build pair',
    data: {
      client: buildInfo,
      api,
    },
  })
}
